import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ViewGRN = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchGRN();
  }, [id]);

  const fetchGRN = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getGRN/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setGrn(data.grn);
      } else {
        setErrorMessage(data.error || "Failed to fetch GRN");
      }
    } catch {
      setErrorMessage("Failed to fetch GRN");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Goods received note not found</p>
        {errorMessage && (
          <p className="text-red-500 mt-2 text-sm">{errorMessage}</p>
        )}
      </div>
    );
  }

  const GRN_STATUSES = {
    completed: { label: "Completed", color: "bg-green-100 text-green-800" },
    partial: { label: "Partial", color: "bg-yellow-100 text-yellow-800" },
    pending: { label: "Pending", color: "bg-gray-100 text-gray-800" },
  };

  const statusConfig = GRN_STATUSES[grn.status] || { label: grn.status || "N/A", color: "bg-gray-100 text-gray-800" };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/GRN")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {grn.grnNumber}
            </h2>
            <p className="text-sm text-gray-500">
              Received {new Date(grn.receivedDate || grn.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
      </div>

      {/* Error */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* GRN Info */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Receipt Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">GRN Number</p>
                <p className="font-medium">{grn.grnNumber}</p>
              </div>
              <div>
                <p className="text-gray-500">Purchase Order</p>
                <p className="font-medium text-primary">
                  {grn.purchaseOrder?.poNumber || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Vendor</p>
                <p className="font-medium">{grn.vendor?.name || grn.purchaseOrder?.vendor?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Received By</p>
                <p className="font-medium">{grn.receivedBy?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Received Date</p>
                <p className="font-medium">
                  {new Date(grn.receivedDate || grn.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Items Received</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase">Qty Received</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase">Accepted</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase">Rejected</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Rejection Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {grn.items?.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <p className="font-medium">{item.item?.name || "N/A"}</p>
                          <p className="text-xs text-gray-400">{item.item?.sku || ""}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-center">{item.qtyReceived || 0}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="text-green-600 font-medium">{item.acceptedQty || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {item.rejectedQty > 0 ? (
                          <span className="text-red-600 font-medium">{item.rejectedQty}</span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {item.rejectionReason || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={2} className="px-4 py-2 text-sm font-semibold text-right">Total:</td>
                    <td className="px-4 py-2 text-sm font-semibold text-center">
                      {grn.items?.reduce((sum, i) => sum + (i.qtyReceived || 0), 0) || 0}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-center text-green-600">
                      {grn.items?.reduce((sum, i) => sum + (i.acceptedQty || 0), 0) || 0}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-center text-red-600">
                      {grn.items?.reduce((sum, i) => sum + (i.rejectedQty || 0), 0) || 0}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes */}
          {grn.notes && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{grn.notes}</p>
            </div>
          )}
        </div>

        {/* Right: Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Items</span>
                <span className="font-medium">{grn.items?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Received</span>
                <span className="font-medium">
                  {grn.items?.reduce((sum, i) => sum + (i.qtyReceived || 0), 0) || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Accepted</span>
                <span className="font-medium text-green-600">
                  {grn.items?.reduce((sum, i) => sum + (i.acceptedQty || 0), 0) || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Rejected</span>
                <span className="font-medium text-red-600">
                  {grn.items?.reduce((sum, i) => sum + (i.rejectedQty || 0), 0) || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Quick link to PO */}
          {grn.purchaseOrder?._id && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Related</h3>
              <button
                onClick={() => navigate(`/PurchaseOrders/View/${grn.purchaseOrder._id}`)}
                className="w-full px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-blue-50"
              >
                View Purchase Order
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewGRN;
